// AddOptionForm — inline "Add option" form for one system_config category.
import { useState } from "react";
import { CONFIG_KEY_PATTERN } from "@dm-lms/shared/validations";
import type { ConfigCategory, SystemConfig } from "@dm-lms/shared/types";
import { postJson } from "../../hooks/useApi.js";

export interface AddOptionFormProps {
  category:  ConfigCategory;
  sortOrder: number;             // position for the new option (count + 1)
  onAdded:   () => void;
  onCancel:  () => void;
}

const INPUT_CLASS =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function AddOptionForm({ category, sortOrder, onAdded, onCancel }: AddOptionFormProps) {
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setError("Label is required");
      return;
    }
    if (!CONFIG_KEY_PATTERN.test(key)) {
      setError("Key must be lowercase letters, digits and underscores");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await postJson<SystemConfig>("/api/config", {
        category,
        key,
        label: trimmedLabel,
        sort_order: sortOrder,
      });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setLabel("");
      setKey("");
      onAdded();
    } catch {
      setError("Unable to add option. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={(event) => { void handleSubmit(event); }} className="mb-4 rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="config-label" className="block text-sm font-medium text-dm-navy">Label</label>
          <input
            id="config-label"
            type="text"
            required
            maxLength={100}
            value={label}
            onChange={(event) => { setLabel(event.target.value); }}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="config-key" className="block text-sm font-medium text-dm-navy">Key</label>
          <input
            id="config-key"
            type="text"
            required
            maxLength={50}
            value={key}
            onChange={(event) => { setKey(event.target.value); }}
            className={`${INPUT_CLASS} font-mono`}
          />
          <p className="mt-1 text-xs text-dm-slate">Lowercase letters, numbers, underscores e.g. court_clerk or 150</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Adding…" : "Add"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-dm-navy"
        >
          Cancel
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </form>
  );
}
